/**
 * Jovii — DRIVING THE APP FROM A TELEVISION REMOTE.
 *
 * A Fire TV Stick has four arrows, an OK and a Back, and nothing else. Before
 * this file the arrows did what arrows do in a browser — they scrolled the
 * window — so the page slid past under a focus that never left `<body>`.
 * Measured on the live site at 1920x1080: 113 focusable elements, five presses
 * of Down, `document.activeElement` still BODY, `scrollY` 0 → 200. There was no
 * way to reach a single poster on the screen.
 *
 * This is the layer that fixes it, and it is about 300 lines rather than a
 * dependency for two reasons. The libraries in this space either polyfill the
 * WICG spatial-navigation draft (which is pure geometry, and pure geometry gets
 * rails wrong — see `dpad-rules.ts`) or they are React/focus-manager frameworks
 * that want to own the component tree. What this app needs is small: measure the
 * page, ask `dpad-rules.ts` where to go, put the focus there, and bring it into
 * view. All four steps are things this codebase already has opinions about.
 *
 * ── IT COSTS NOTHING UNTIL A KEY IS PRESSED ────────────────────────────────
 *
 * One `keydown` listener on `window`, and it does nothing at all until an arrow
 * arrives. No user-agent sniffing, no `matchMedia`, no "is this a TV" guess: a
 * real directional key event is the only signal, and it is a reliable one,
 * because the thing that emits it is a person pressing a direction. A phone and
 * a tablet never emit one and are therefore untouched — the same bundle, the
 * same DOM, the same behaviour they had before this file existed.
 *
 * The first arrow also sets `data-dpad` on <html>, which is what the stylesheet
 * hangs the fatter focus ring off. Three metres away a 2px outline is a rumour.
 *
 * ── WHAT IT WILL NOT DO ────────────────────────────────────────────────────
 *
 *  • It never touches Tab. Every control on every screen keeps the tab order it
 *    had, which matters because the arrow rules deliberately make a few things
 *    unreachable (a poster's save button is drawn ON the poster's link, so it is
 *    to the right of nothing).
 *  • It never handles a key another handler has already claimed. `Menu`, the
 *    scrubber, the trailer sheet and the chat box all `preventDefault()` on the
 *    keys they own, and `defaultPrevented` is the first thing checked here.
 *  • It stands down entirely on a screen that says it owns its own arrows
 *    (`data-dpad-owner`). The player does: Left and Right there mean SEEK, which
 *    is not a spatial idea, and no amount of geometry produces it.
 *  • It gives the key back to the browser when nothing lies in the direction
 *    pressed. That is what keeps a long page of prose scrollable with the arrows
 *    it has always been scrollable with, and it is the escape hatch that gets a
 *    reader past a rail whose lazy contents have not arrived yet.
 *
 * ── THE ATTRIBUTE CONTRACT ─────────────────────────────────────────────────
 *
 *   data-dpad="row"     a rail. Left/Right stay inside it; Up/Down leave it and
 *   data-dpad="col"     look for another one. 'col' is the same idea stood on
 *                       end, for a stacked list whose Left/Right must not wander.
 *   data-dpad-skip      this subtree is not a destination. Rail scroll arrows
 *                       wear it: on a remote, Right IS the scroll arrow.
 *   data-dpad-chrome    app furniture. Reachable, but never where the FIRST
 *                       press of the remote lands.
 *   data-dpad-scope     while this is on screen, nothing outside it is reachable.
 *                       Sheets and dialogs wear it.
 *   data-dpad-owner     this screen handles its own arrows; stand down.
 */

import { back, historyKey } from './nav.svelte';
import { type Direction, type NavRect, entryId, nextId, score } from './dpad-rules';

export { type Direction } from './dpad-rules';

/* ────────────────────────────────────────────────────────────────────────────
 * The numbers
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * How much of the next tile stays showing when a rail is scrolled to follow the
 * focus. A cut-off poster is what tells you the row continues — the same
 * argument `Rail.svelte` makes for bleeding to the screen edge — and a rail that
 * scrolls the focused card flush to its own edge destroys that signal on every
 * press.
 */
const RAIL_PEEK = 56;

/** Clear of the floating pills at the top of the screen. `--bar-h` plus a breath. */
const HEAD_ROOM = 88;

/** Clear of the phone's tab bar, and of the bottom of a television's overscan. */
const FOOT_ROOM = 96;

/**
 * Where a row that has to move ends up: 42% down the screen, not 50%.
 *
 * A rail is a heading with cards under it, and the focus is on the cards. Centre
 * the CARD and the heading it belongs to sits mid-screen with a third of the
 * viewport empty above it; 42% puts the card just above the middle, which leaves
 * the heading visible and the next rail peeking in at the bottom. That peek is
 * the whole reason a television shelf feels navigable.
 */
const REST_AT = 0.42;

/**
 * Below this gap between presses the remote is being HELD DOWN, and smooth
 * scrolling stops helping: each new animation retargets the last, so the page
 * lags behind the focus by however long the easing takes. Above it, one press is
 * one considered movement and the easing is what makes it readable.
 */
const REPEAT_MS = 260;

/**
 * A TELEVISION NEVER EASES.
 *
 * The owner, on a Fire TV Stick: "there is quite a lot of latency between the
 * remote's D-pad and what I see on screen". The handler was already local — this
 * file is one `keydown` listener and nothing crosses the network — so the delay
 * was work, and a third of it was this: a smooth scroll is a 300 ms animation
 * that the stick's SoC has to paint every frame of, and the NEXT press lands in
 * the middle of it and has to compete for the same main thread. Two presses in
 * and the page is visibly behind the remote.
 *
 * A desk keeps the easing: a mouse-and-keyboard viewer is not fighting a weak
 * chip and the glide is what makes a jump readable. A television gets the
 * highlight and the page in the same frame, which is what "zero latency" means
 * when the frame is the unit.
 */
function onTv(): boolean {
  return typeof document !== 'undefined' && document.documentElement.dataset.tv === 'on';
}

/* ────────────────────────────────────────────────────────────────────────────
 * Which elements are candidates
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * The same list the repro used to count 113 elements, minus `[tabindex="-1"]`,
 * which is the standard way of saying "focusable by script, not by a user" and
 * is worn here by menu items (their own Up/Down owns them), the chat log, and
 * the trailer iframe's wrapper.
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

/**
 * IS IT REALLY THERE.
 *
 * Three separate ways this app hides a control while leaving it in the DOM, and
 * a focus that lands on any of them is a focus the viewer cannot see:
 *
 *  1. `display: none` / an empty rect — a rail that has not answered yet.
 *  2. `opacity: 0` on an ancestor — how the player's transport bar and the call
 *     dock fade out. `checkVisibility` is the only cheap way to see through an
 *     ancestor's opacity; where it is missing (an older WebView) the rect test
 *     and the `pointer-events` test below still catch both real cases.
 *  3. `pointer-events: none` — which in this codebase is precisely the flag for
 *     "drawn but not live": the hidden player chrome sets it, the dimmed call
 *     dock sets it, and — the reason it is read off the ELEMENT and never off an
 *     ancestor — the top bar sets it on itself and hands it back to each pill,
 *     so the pills compute `auto` and stay reachable while the invisible sheet
 *     of glass between them does not.
 *
 * All three are STYLE, which is why they are re-asked on every press. The
 * structural questions that used to be asked here too — skip, inert,
 * aria-hidden — moved into the catalogue below, because they cannot change
 * without the DOM changing.
 */
function reachable(el: HTMLElement, rect: DOMRect): boolean {
  if (rect.width <= 0 || rect.height <= 0) return false;

  const check = (el as HTMLElement & { checkVisibility?: (o: object) => boolean }).checkVisibility;
  if (typeof check === 'function') {
    if (!check.call(el, { opacityProperty: true, visibilityProperty: true })) return false;
  }

  const style = getComputedStyle(el);
  return style.pointerEvents !== 'none' && style.visibility !== 'hidden';
}

/* ────────────────────────────────────────────────────────────────────────────
 * THE CATALOGUE — what is on this screen, worked out once instead of per press
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * WHAT A KEYPRESS USED TO COST, and why this exists.
 *
 * Profiled on the live shelf at 1920x1080 with the CPU throttled 6x to stand in
 * for a Fire TV Stick: 101 candidates, and every one of them was asked five
 * separate `closest()` questions (skip, aria-hidden, inert, which group, is it
 * chrome) on every single press. Each `closest()` walks to the root, so that is
 * five full ancestor walks per element — 2.5 ms of pure tree walking — plus
 * 1.9 ms to run the selector again and 0.6 ms to re-find the scope. Five
 * milliseconds a press, at 6x, spent re-deriving facts that had not changed.
 *
 * None of them CAN change without the DOM changing, so they are derived once
 * and thrown away when it does. Two further economies fall out of doing it here:
 * the five walks collapse into ONE walk that answers all five questions on the
 * way up, and an element that is skipped or inert never enters the list at all,
 * so it is not measured either.
 *
 * WHAT INVALIDATES IT, and — just as important — what does not. `class` and
 * `style` are deliberately NOT watched: a poster lifts and a rim appears on
 * every focus move, so watching them would throw the catalogue away on exactly
 * the presses it exists to make cheap. Nothing in `FOCUSABLE` is selected by
 * class, and nothing in the walk below is decided by one — every attribute that
 * could change an answer is in `STRUCTURAL`. Whether an element is VISIBLE is a
 * style question and is still asked fresh on every press, in `reachable`.
 */
interface Entry {
  el: HTMLElement;
  id: string;
  group: HTMLElement | null;
  groupId: string | null;
  axis: 'row' | 'col' | null;
  chrome: boolean;
}

const STRUCTURAL = [
  'hidden',
  'inert',
  'disabled',
  'tabindex',
  'contenteditable',
  'aria-hidden',
  'href',
  'open',
  'data-dpad',
  'data-dpad-skip',
  'data-dpad-chrome',
  'data-dpad-scope',
  'data-dpad-owner'
];

let catalogue: Entry[] | null = null;
let catalogueRoot: ParentNode | null = null;
let byNode: Map<HTMLElement, Entry> | null = null;
let scopes: HTMLElement[] | null = null;
let observer: MutationObserver | null = null;

function forget(): void {
  catalogue = null;
  catalogueRoot = null;
  byNode = null;
  scopes = null;
}

function watchDom(): void {
  if (observer !== null || typeof MutationObserver === 'undefined') return;
  observer = new MutationObserver(() => {
    forget();
    // Content arriving late — an award rail answering, a collection grid still
    // streaming in — reflows the page UNDER the focus. See `keepInView`.
    settleSoon();
  });
  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: STRUCTURAL
  });
  // A resize moves nothing in or out of the DOM but it re-flows every rail, and
  // the cheapest correct answer to that is to start again.
  window.addEventListener('resize', forget, { passive: true });
  window.addEventListener('orientationchange', forget, { passive: true });
}

/**
 * WHERE THE SEARCH IS ALLOWED TO LOOK.
 *
 * The last `[data-dpad-scope]` in the document, or the last open `<dialog>`, or
 * the whole document. Last and not first, because a scope inside a scope is the
 * one on top — a source picker opened from a sheet — and the topmost one is the
 * only one the viewer can see.
 *
 * A native `<dialog>` opened with `showModal()` already traps TAB. It does not
 * trap `.focus()`, which is what this file does, so it has to be honoured here
 * too or the remote walks straight out of a modal and behind it.
 *
 * The LIST is cached — a scope arriving or leaving is a DOM change — but which
 * of them is live is re-measured, because a scope that is present and collapsed
 * to nothing is a sheet mid-transition and that is a style question.
 */
function scope(): ParentNode {
  if (scopes === null) {
    scopes = [...document.querySelectorAll<HTMLElement>('[data-dpad-scope], dialog[open]')];
    watchDom();
  }
  for (let i = scopes.length - 1; i >= 0; i -= 1) {
    const el = scopes[i]!;
    if (!el.isConnected) continue;
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) return el;
  }
  return document;
}

/**
 * ONE WALK UP, FIVE ANSWERS. See the note above `Entry` for why this is not
 * five `closest()` calls, and why it is safe to keep the result.
 */
function catalogueOf(root: ParentNode): Entry[] {
  if (catalogue !== null && catalogueRoot === root) return catalogue;

  const out: Entry[] = [];
  for (const el of root.querySelectorAll<HTMLElement>(FOCUSABLE)) {
    let group: HTMLElement | null = null;
    let chrome = false;
    let blocked = false;

    for (let node: HTMLElement | null = el; node !== null; node = node.parentElement) {
      if (node.hasAttribute('data-dpad-skip') || node.hasAttribute('inert')) {
        blocked = true;
        break;
      }
      if (node.getAttribute('aria-hidden') === 'true') {
        blocked = true;
        break;
      }
      if (group === null) {
        const axis = node.getAttribute('data-dpad');
        if (axis === 'row' || axis === 'col') group = node;
      }
      if (!chrome && node.hasAttribute('data-dpad-chrome')) chrome = true;
    }
    if (blocked) continue;

    out.push({
      el,
      id: idOf(el),
      group,
      groupId: group === null ? null : idOf(group),
      axis: group === null ? null : group.getAttribute('data-dpad') === 'col' ? 'col' : 'row',
      chrome
    });
  }

  catalogue = out;
  catalogueRoot = root;
  byNode = new Map(out.map((e) => [e.el, e]));
  watchDom();
  return out;
}

/** The catalogue entry for whatever holds the focus, or null. */
function entryFor(el: Element | null): Entry | null {
  if (!(el instanceof HTMLElement)) return null;
  catalogueOf(scope());
  return byNode?.get(el) ?? null;
}

/** Stable ids for the rules module, which deals in strings and not in nodes. */
let serial = 0;
const ids = new WeakMap<Element, string>();
function idOf(el: Element): string {
  let id = ids.get(el);
  if (id === undefined) {
    serial += 1;
    id = `d${serial}`;
    ids.set(el, id);
  }
  return id;
}

interface Measured {
  rects: NavRect[];
  nodes: Map<string, HTMLElement>;
}

/**
 * THE BAND — measure the whole page, STYLE-CHECK only what could win.
 *
 * The per-candidate work a press pays twice over — `getBoundingClientRect`,
 * then `reachable()` with its `checkVisibility` ancestor walk and a
 * `getComputedStyle` — scaled with the whole pool. But a press has a
 * DIRECTION, and `nextId` will never choose a candidate whose travel that way
 * exceeds anything nearby: so candidates whose rects lie outside a generous
 * band in the pressed direction are dropped BEFORE the style checks, on the
 * rect alone. Measured (2026-08-19, dpad-bench.html, 6x CDP throttle,
 * 504-tile grid + two rails, nested tile frames): press median 35.1 ms →
 * 21.2 ms, with an IDENTICAL focus trail across a 15-press walk in every
 * direction; at 1008 tiles 47.0 ms → 42.6 ms — the residue is the rect sweep
 * itself, which stays O(pool) by design (rects are the cheap half, and the
 * fallback proof below needs them all).
 *
 * WHY THIS CANNOT CHANGE AN ANSWER. The band keeps everything with
 * travel ≥ −BAND_BEHIND (⊇ the rules' own −EDGE_SLACK cut) out to
 * travel ≤ bandAhead(). A candidate beyond that has score > bandAhead()
 * (score ≥ travel, `dpad-rules.ts`). `move()` accepts a pruned winner ONLY
 * when its score is ≤ bandAhead() — so nothing the band excluded could have
 * beaten it inside its tier — AND when the winner's tier proves the higher
 * tiers were genuinely empty (a grouped winner from a grouped press; never a
 * chrome winner for a content press). Any other outcome, including finding
 * nothing at all, re-measures the whole page exactly as before — the slow
 * path is the old path, taken only when the fast one cannot prove itself.
 */
interface Band {
  dir: Direction;
  from: DOMRect;
}

/** Travel behind the focus that is still kept: covers the rules' EDGE_SLACK
 *  overlap allowance with room to spare. */
const BAND_BEHIND = 48;

/** How far ahead (in travel) the band reaches: two screens in the pressed
 *  axis. Rows and rails are contiguous, so the real winner is virtually
 *  always inside; the acceptance check in `move()` is what makes "virtually"
 *  into "provably or re-measured". */
function bandAhead(dir: Direction): number {
  return 2 * (dir === 'left' || dir === 'right' ? window.innerWidth : window.innerHeight);
}

function outsideBand(rect: DOMRect, band: Band): boolean {
  const ahead = bandAhead(band.dir);
  const f = band.from;
  switch (band.dir) {
    case 'down':
      return rect.top < f.bottom - BAND_BEHIND || rect.top > f.bottom + ahead;
    case 'up':
      return rect.bottom > f.top + BAND_BEHIND || rect.bottom < f.top - ahead;
    case 'right':
      return rect.left < f.right - BAND_BEHIND || rect.left > f.right + ahead;
    case 'left':
      return rect.right > f.left + BAND_BEHIND || rect.right < f.left - ahead;
  }
}

/**
 * Measure every reachable candidate inside the current scope, once.
 *
 * ONLY THE THINGS THAT MOVE ARE READ HERE. Which elements exist, which group
 * each belongs to and whether it is chrome all come from the catalogue above;
 * what is left is geometry and visibility, both of which change between one
 * press and the next and neither of which can be cached honestly.
 *
 * The `clipped` flag is worked out against the GROUP's own box rather than
 * against a general walk of scrolling ancestors, and that is a deliberate
 * shortcut: in this app every clipping scroller a candidate can hide inside is
 * also a d-pad group — a rail's track, the cast row, the genre chips, the season
 * tabs — because a horizontal scroller is exactly the thing that needed Left and
 * Right constraining in the first place. One `getBoundingClientRect()` per
 * group, cached, instead of a `getComputedStyle()` walk per candidate.
 *
 * Every read in here is a read, with no write between any two of them, which is
 * what keeps the whole press to a single layout — see `plan()` for the other
 * half of that bargain.
 */
function measure(only: HTMLElement | null = null, band: Band | null = null, keep: HTMLElement | null = null): Measured {
  const nodes = new Map<string, HTMLElement>();
  const rects: NavRect[] = [];
  const boxes = new Map<HTMLElement, DOMRect>();
  for (const entry of catalogueOf(scope())) {
    if (only !== null && entry.group !== only) continue;
    const rect = entry.el.getBoundingClientRect();
    // The band cut, on the rect alone, BEFORE the per-element style checks —
    // see `Band`. The focused element itself is always kept: `nextId` needs
    // its rectangle whatever direction was pressed.
    if (band !== null && entry.el !== keep && outsideBand(rect, band)) continue;
    if (!reachable(entry.el, rect)) continue;
    let clipped = false;
    if (entry.group !== null) {
      let box = boxes.get(entry.group);
      if (box === undefined) {
        box = entry.group.getBoundingClientRect();
        boxes.set(entry.group, box);
      }
      clipped = rect.right <= box.left || rect.left >= box.right || rect.bottom <= box.top || rect.top >= box.bottom;
    }
    nodes.set(entry.id, entry.el);
    rects.push({
      id: entry.id,
      left: rect.left,
      top: rect.top,
      right: rect.right,
      bottom: rect.bottom,
      group: entry.groupId,
      axis: entry.axis,
      chrome: entry.chrome,
      clipped
    });
  }
  return { rects, nodes };
}

/* ────────────────────────────────────────────────────────────────────────────
 * Bringing the focus into view
 * ──────────────────────────────────────────────────────────────────────────── */

let lastMove = 0;

/** Asked once. Building a MediaQueryList per keypress is not free on a stick. */
let calm: MediaQueryList | null | undefined;
function reducedMotion(): boolean {
  if (calm === undefined) calm = typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;
  return calm !== null && calm.matches;
}

/** Held-down remote: no easing, or the page never catches up. See `REPEAT_MS`. */
function easing(): ScrollBehavior {
  const now = Date.now();
  const quick = now - lastMove < REPEAT_MS;
  lastMove = now;
  if (quick) return 'auto';
  if (onTv()) return 'auto';
  return reducedMotion() ? 'auto' : 'smooth';
}

/** The scrollers between `el` and the page, innermost first. A rail is one. */
function scrollers(el: HTMLElement): HTMLElement[] {
  const out: HTMLElement[] = [];
  let node = el.parentElement;
  while (node !== null && node !== document.body && node !== document.documentElement) {
    const style = getComputedStyle(node);
    const scrollsX = /^(auto|scroll)$/.test(style.overflowX) && node.scrollWidth > node.clientWidth + 1;
    const scrollsY = /^(auto|scroll)$/.test(style.overflowY) && node.scrollHeight > node.clientHeight + 1;
    if (scrollsX || scrollsY) out.push(node);
    node = node.parentElement;
  }
  return out;
}

/** One scroller and how far it has to move. Nothing is written while planning. */
interface Nudge {
  box: HTMLElement | Window;
  left: number;
  top: number;
}

/**
 * PUT IT SOMEWHERE THE VIEWER CAN SEE IT, and move as little as possible doing so.
 *
 * Deliberately not `scrollIntoView()`. Its `nearest` does nothing when an
 * element is already fully visible — which is right — but the moment it is one
 * pixel short it slams the element flush against the edge it came in from, with
 * no peek of the next tile and no room above the heading. Its `center` centres
 * every axis every time, which on a rail means the row jumps sideways under a
 * viewer who only asked to move one card along. So:
 *
 *   • Inside a rail, scroll only far enough to leave `RAIL_PEEK` showing beyond
 *     the focused card. A rail that is already comfortable does not move at all.
 *   • Down the page, do nothing while the focus sits in the comfortable band;
 *     when it leaves, settle it at `REST_AT`, which is a real move rather than a
 *     grudging one — a television viewer wants the row they are on to arrive
 *     somewhere restful, not to creep up to the very bottom of the screen and
 *     stay there.
 *
 * ── IT DECIDES, IT DOES NOT MOVE ───────────────────────────────────────────
 *
 * This used to be one function that measured, scrolled, measured again, scrolled
 * again and finally measured a third time to remember where it had been. Every
 * one of those measurements after the first came AFTER a write, and a read after
 * a write is a forced synchronous layout — the browser has to lay the whole page
 * out again before it can answer. Three of them per press, plus a fourth caused
 * by `.focus()` invalidating style before any of them ran. On the throttled
 * profile that was the larger half of a 45 ms keypress.
 *
 * So it returns a list instead. The caller has already measured the target's
 * rectangle for the geometry; this walks the scrollers deciding what each must
 * do, projecting the element forward by the previous decision rather than asking
 * the browser again, and hands back the moves. Reads all together, writes all
 * together, one layout for the press.
 */
function plan(el: HTMLElement, start: NavRect): Nudge[] {
  const out: Nudge[] = [];
  let { left, top, right, bottom } = start;

  for (const box of scrollers(el)) {
    const outer = box.getBoundingClientRect();
    let dx = 0;
    let dy = 0;

    if (box.scrollWidth > box.clientWidth + 1) {
      // Never demand more peek than the scroller can give: a card wider than its
      // own rail (an expanded trailer) would otherwise oscillate for ever.
      const peek = Math.min(RAIL_PEEK, Math.max(0, (outer.width - (right - left)) / 2));
      if (left < outer.left + peek) dx = left - outer.left - peek;
      else if (right > outer.right - peek) dx = right - outer.right + peek;
      // Clamped to what the scroller can actually do, because the projection
      // below has to match what will really happen — a rail already at its end
      // does not move, and pretending it did would misplace the page scroll.
      dx = Math.max(-box.scrollLeft, Math.min(dx, box.scrollWidth - box.clientWidth - box.scrollLeft));
    }
    if (box.scrollHeight > box.clientHeight + 1) {
      if (top < outer.top) dy = top - outer.top;
      else if (bottom > outer.bottom) dy = bottom - outer.bottom;
      dy = Math.max(-box.scrollTop, Math.min(dy, box.scrollHeight - box.clientHeight - box.scrollTop));
    }

    if (dx !== 0 || dy !== 0) {
      out.push({ box, left: dx, top: dy });
      // The scroller moves that way, so the element moves the other way inside
      // it — which is exactly what the old code paid a layout to be told.
      left -= dx;
      right -= dx;
      top -= dy;
      bottom -= dy;
    }
  }

  const view = window.innerHeight;
  const roof = HEAD_ROOM;
  const floor = view - FOOT_ROOM;
  if (top >= roof && bottom <= floor) return out;

  // A banner is taller than the comfortable band and can never sit inside it;
  // aligning its top is the only answer that does not hide its own caption.
  const dy = bottom - top > floor - roof ? top - roof : (top + bottom) / 2 - view * REST_AT;
  if (Math.abs(dy) > 1) out.push({ box: window, left: 0, top: dy });
  return out;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Moving
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * TRUE WHILE THIS FILE IS THE ONE CALLING `.focus()`.
 *
 * `focus.svelte.ts` scrolls a card into view when it takes keyboard focus,
 * because tabbing along a rail did not always move the browser's own focus
 * scrolling. That fix and this file's `plan`/`land` are two answers to one
 * question, and running both means an instant snap to the edge followed by a
 * smooth glide to the middle — one press, two visible movements. This flag lets
 * the older answer stand aside for the one that knows which direction was
 * pressed.
 *
 * It also matters more than it used to: `land` now focuses BEFORE it scrolls, so
 * the `focusin` this raises arrives while the page is still where the press
 * found it. Without the flag the browser's answer would win the race outright.
 */
let claiming = false;
export function movingFocus(): boolean {
  return claiming;
}

/**
 * WHERE THE REMOTE WAS ON EACH SCREEN IT HAS LEFT.
 *
 * Not the element — that is gone; a rail is rebuilt from scratch when you come
 * back to it. A POINT, in document coordinates, and on return the candidate
 * nearest to it wins. The shelf is seeded by the day and not shuffled
 * (`shelf.ts`), so a tile really is in the same place on the way back, and the
 * fuzziness is what makes this survive a rail that has since loaded two more
 * cards or a banner that has moved on to a different slide.
 *
 * Keyed with `nav.svelte.ts`'s own history key, so this map and the scroll
 * memory next to it always agree about which entry is which. Restoring the
 * scroll but not the focus is worse than restoring neither: the page comes back
 * exactly where it was and the highlight is somewhere else entirely.
 */
const memory = new Map<string, { x: number; y: number }>();

/** How far a remembered point may have moved and still be the same tile. */
const MEMORY_SLACK = 260;

/**
 * Taken from the rectangle the press already measured, not from a fresh one.
 * Document coordinates are invariant under a page scroll, so measuring before
 * the scroll and measuring after it give the same answer — and the "after" cost
 * a whole layout.
 */
function remember(box: NavRect): void {
  if (!remote) return;
  memory.set(historyKey(), {
    x: (box.left + box.right) / 2 + window.scrollX,
    y: (box.top + box.bottom) / 2 + window.scrollY
  });
}

/**
 * THE ONLY PLACE THE FOCUS MOVES, and the order of these four steps is the
 * whole latency fix. Decide (reads), remember (no reads), focus (write), scroll
 * (writes). Nothing reads the layout after something has written to it, so the
 * browser lays the page out once for the entire keypress.
 */
function land(el: HTMLElement, box: NavRect, behavior: ScrollBehavior): void {
  const moves = plan(el, box);
  remember(box);
  claiming = true;
  try {
    // `preventScroll` is the same argument again: the BROWSER also scrolls on
    // focus, and its idea of where is the edge of the nearest scrollport.
    el.focus({ preventScroll: true });
  } finally {
    claiming = false;
  }
  for (const move of moves) move.box.scrollBy({ left: move.left, top: move.top, behavior });
  // The plan above was measured BEFORE the page reacted to the focus arriving —
  // the details strip under the old rail collapses and the new one opens in the
  // microtask after `.focus()`, which on a television is an instant snap
  // (`Rail.svelte` kills the strip's easing there). Measured on the shelf at
  // 1280x720: every rail-to-rail hop landed the tile a strip-height too high —
  // screen y=50, under the floating pills — and stayed there until the NEXT
  // press re-corrected it. So the landing is re-checked once the DOM has had
  // its say.
  settleSoon();
}

/**
 * KEEP THE FOCUSED THING WHERE THE VIEWER CAN SEE IT — television only.
 *
 * On a desktop the focus follows the pointer and a layout shift under it is an
 * annoyance; on a television the focus IS the viewer's position, and a page
 * that moves underneath it strands them. Three measured ways this app does it:
 *
 *   1. An award rail renders nothing until its catalogue lookups land, then
 *      opens to full height: walking down Home, the focused plate was shoved
 *      clean off the screen (measured: focus offscreen, and the next Up had to
 *      scroll DOWN 367px to reach the rail that had just materialised above).
 *   2. The details strip under a rail swaps rails AFTER the press that moved
 *      the focus was planned — every hop landed short. See `land`.
 *   3. A card that opens on dwell grows 2.9x sideways; parked on the last tile
 *      of a rail, it grew straight past the right edge of the panel and sat
 *      there half-visible until another key arrived.
 *
 * The answer is one cheap re-check, rAF-throttled, fed by the three signals
 * that cover those cases: every landing, layout-affecting `transitionend`, and
 * the DOM mutations the catalogue watcher already observes. It reuses `plan`,
 * so "in view" means the same comfortable band a press would have chosen, and
 * on a television every correction is instant, so there is nothing mid-flight
 * for it to fight. It never runs off-television: a desktop's smooth scrolls
 * WOULD still be mid-flight, and a keyboard user is not lost for a shift a
 * pointer can undo.
 */
let settling: number | null = null;
function settleSoon(): void {
  if (settling !== null || typeof requestAnimationFrame !== 'function') return;
  if (!remote || !onTv()) return;
  settling = requestAnimationFrame(() => {
    settling = null;
    keepInView();
  });
}

function keepInView(): void {
  if (!remote || !onTv() || claiming) return;
  const el = document.activeElement;
  if (!(el instanceof HTMLElement) || el === document.body || !el.isConnected) return;
  const r = el.getBoundingClientRect();
  if (r.width <= 0 || r.height <= 0) return;
  const moves = plan(el, { id: 'held', left: r.left, top: r.top, right: r.right, bottom: r.bottom });
  for (const move of moves) move.box.scrollBy({ left: move.left, top: move.top, behavior: 'auto' });
}

/** The transitions whose END means layout has finished moving under the focus. */
const LAYOUT_PROPS = new Set(['width', 'height', 'grid-template-rows']);
function onTransitionEnd(event: TransitionEvent): void {
  if (LAYOUT_PROPS.has(event.propertyName)) settleSoon();
}

/**
 * ONE STEP. Returns false when nothing lies that way, which is the caller's cue
 * to hand the key back to the browser.
 *
 * Exported because the watch screen owns its own arrows and still wants the
 * spatial answer for the ones it does not spend on seeking.
 */
export function move(dir: Direction): boolean {
  const active = document.activeElement;

  /*
   * ALONG A RAIL, ONLY THE RAIL IS MEASURED.
   *
   * `nextId` filters the pool down to `r.group === from.group` before it looks
   * at a single rectangle when the press runs along the group's own axis — Right
   * from a poster is another poster in that row or it is nothing, by rule and not
   * by geometry. So measuring the other twelve rails to answer it is ninety
   * elements of work thrown away, on the presses a viewer makes most: walking
   * along a row is what browsing a shelf IS.
   *
   * The narrow answer is IDENTICAL, not merely close — same filter, same pool —
   * with one thing to guard: `nextId` falls back to `entryId` over the whole
   * pool if the focused element is not in it, which a narrow pool would answer
   * wrongly. So if the focus is not in what we measured, we start again wide.
   *
   * ROWS ONLY. A 'col' group's vertical dead ends open back onto the page
   * (`dpad-rules.ts`): Up from the first episode of a season is the season
   * picker above it, and answering that needs the rest of the page in the
   * pool — measured with the narrow pool, Up sat pinned on episode one while
   * the browser crept the page 40px a press. A stacked list is never the
   * hundred-element case the narrowing exists for.
   */
  const here = entryFor(active);
  const along = here !== null && here.group !== null && (here.axis === 'row') === (dir === 'left' || dir === 'right');
  const narrow = along && here !== null && here.axis === 'row';

  /* ACROSS the page, only the BAND is style-checked (see `Band` above): the
     rects still come cheap in one layout, but the ancestor-walking visibility
     checks — the actual cost of a 500-tile grid — run only on what could win.
     The acceptance check below is what keeps the answer IDENTICAL; anything
     it cannot prove re-measures wide, which is exactly the old cost. */
  const banded = !narrow && here !== null && active instanceof HTMLElement;
  let { rects, nodes } = banded
    ? measure(null, { dir, from: active.getBoundingClientRect() }, active)
    : measure(narrow && here !== null ? here.group : null);
  if (narrow && here !== null && !nodes.has(here.id)) ({ rects, nodes } = measure());
  if (rects.length === 0 && !banded) return false;

  let from = active instanceof HTMLElement && nodes.has(idOf(active)) ? idOf(active) : null;
  const view = { width: window.innerWidth, height: window.innerHeight };

  let target = nextId(rects, from, dir, view);
  if (banded) {
    const fromBox = from === null ? null : (rects.find((r) => r.id === from) ?? null);
    const win = target === null ? null : (rects.find((r) => r.id === target) ?? null);
    const proven =
      fromBox !== null &&
      win !== null &&
      // Nothing the band excluded could have beaten this winner in its tier…
      score(fromBox, win, dir) <= bandAhead(dir) &&
      // …and no HIGHER tier the band might have emptied could have answered:
      // a content press must not settle for chrome, and a press out of a
      // group must land in one (the grouped tier is consulted first there).
      (win.chrome !== true || (fromBox.chrome === true && (dir === 'left' || dir === 'right'))) &&
      (fromBox.group == null || win.group != null);
    if (!proven) {
      // The fast path could not prove itself (heal, an empty band, a distant
      // winner): the whole page, exactly as every press paid before.
      ({ rects, nodes } = measure());
      if (rects.length === 0) return false;
      from = active instanceof HTMLElement && nodes.has(idOf(active)) ? idOf(active) : null;
      target = nextId(rects, from, dir, view);
    }
  }
  if (target === null) return false;
  const el = nodes.get(target);
  if (el === undefined) return false;
  const box = rects.find((r) => r.id === target);
  if (box === undefined) return false;

  land(el, box, easing());
  return true;
}

/**
 * Put the focus somewhere sensible on this screen. Used by the first press of
 * the remote and by anything that has just replaced the screen under it.
 */
export function enter(): boolean {
  const { rects, nodes } = measure();

  /* Coming BACK to a screen: the tile the remote was on, if anything is still
     near where it was. `nav.svelte.ts` has already put the scroll back, so this
     is measured against a page that is where it was when we left it. */
  const winner = nearMemory(rects);
  if (winner !== null) {
    const el = nodes.get(winner.id);
    if (el !== undefined) {
      land(el, winner, 'auto');
      return true;
    }
  }

  const id = entryId(rects, { width: window.innerWidth, height: window.innerHeight });
  if (id === null) return false;
  const el = nodes.get(id);
  const box = rects.find((r) => r.id === id);
  if (el === undefined || box === undefined) return false;
  land(el, box, easing());
  return true;
}

/** How long to wait for a screen to have something on it worth focusing. */
const SCREEN_TRIES = 12;
const SCREEN_STEP_MS = 120;
/**
 * How long to wait when the remote HAD a place on this screen. Coming Back from
 * a title, the shelf re-fetches its catalogue, and for a second or two the only
 * candidates are the bar's pills. Settling for those hands the viewer's place
 * to the search pill — measured: Back from a title page, scroll restored to the
 * tile, focus on "Search films and series" — and once something holds the
 * focus, `focusScreen` rightly never takes it away, so the place is lost for
 * good. A remembered place is worth waiting a slow catalogue out for.
 */
const SCREEN_HELD_TRIES = 40;

/** The remembered point's nearest live candidate, or null. Shared by `enter`. */
function nearMemory(rects: readonly NavRect[]): NavRect | null {
  const was = memory.get(historyKey());
  if (was === undefined) return null;
  let winner: NavRect | null = null;
  let best = MEMORY_SLACK;
  for (const r of rects) {
    const dx = (r.left + r.right) / 2 + window.scrollX - was.x;
    const dy = (r.top + r.bottom) / 2 + window.scrollY - was.y;
    const d = Math.hypot(dx, dy);
    if (d < best) {
      best = d;
      winner = r;
    }
  }
  return winner;
}

/**
 * A NEW SCREEN NEEDS A NEW FOCUS — and only a remote needs telling.
 *
 * Pressing OK on a poster tears the shelf down and builds a title page, so the
 * element that held the focus no longer exists and the focus falls back to
 * `<body>`. With a mouse that is invisible and correct. With a remote it is a
 * dead end: the viewer pressed OK, the screen changed, and the next press of Down
 * has to start the whole search again from nothing.
 *
 * IT WAITS FOR THE SCREEN TO HAVE CONTENT ON IT. Every screen in this app has a
 * top bar from the first frame, so focusing "the first thing" immediately would
 * reliably land on the avatar menu and stay there while the film's own Play
 * button rendered underneath. So it retries — up to a second and a half, which
 * is a slow catalogue on a bad connection — until something that is not chrome
 * exists, and only settles for the bar if nothing ever does.
 *
 * It never takes the focus from somebody who already has it: a sheet that
 * autofocuses its own panel, a search field that asked for the caret.
 */
export function focusScreen(): void {
  if (!remote) return;
  let tries = 0;
  const attempt = (): void => {
    if (!remote) return;
    const active = document.activeElement;
    if (active instanceof HTMLElement && active !== document.body) return;
    tries += 1;
    const { rects } = measure();
    /* A screen the remote has a place on gets more patience, and gets it until
       THAT PLACE is back — the shelf's "All" links render a beat before its
       tiles do, and settling for "some content exists" would still put the
       focus a screen away from where the viewer left it. */
    const held = nearMemory(rects) !== null;
    const patience = memory.has(historyKey()) ? SCREEN_HELD_TRIES : SCREEN_TRIES;
    const content = memory.has(historyKey()) ? held : rects.some((r) => r.chrome !== true);
    if (content || tries >= patience) {
      enter();
      return;
    }
    setTimeout(attempt, SCREEN_STEP_MS);
  };
  requestAnimationFrame(attempt);
}

/* ────────────────────────────────────────────────────────────────────────────
 * The keys
 * ──────────────────────────────────────────────────────────────────────────── */

const ARROWS: Record<string, Direction> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
  // A few WebViews and set-top browsers still emit the pre-DOM4 names.
  Up: 'up',
  Down: 'down',
  Left: 'left',
  Right: 'right'
};

/** Fire TV's Back, in every spelling a WebView has been seen to use for it. */
const BACKS = new Set(['Escape', 'Esc', 'GoBack', 'BrowserBack', 'Back']);

/** Input types whose arrows are a caret and not a direction. */
const TEXTUAL = new Set([
  'text',
  'search',
  'email',
  'password',
  'tel',
  'url',
  'number',
  'date',
  'time',
  'datetime-local',
  'month',
  'week'
]);

/**
 * TYPING MUST NEVER BE NAVIGATION — BUT A CONTROL MUST NEVER BE A TRAP EITHER,
 * and that tension is why this takes a direction.
 *
 * A control owns the AXIS IT USES and no more:
 *
 *  • A multi-line box and a radio group own both. Up in a `<textarea>` is the
 *    line above; Up in a radio group is the previous option. There is nowhere
 *    left for a remote to go, and both are reached and left with Tab.
 *  • A single-line field, a `<select>` and a slider own the HORIZONTAL only.
 *    Left and Right are the caret, the previous option, ten seconds of film —
 *    all real. Up and Down are not: in a one-line field they jump the caret to
 *    an end you can already reach, and on the other two they duplicate Left and
 *    Right exactly. Measured on /search at 1920x1080, treating the search box as
 *    wholly off limits meant four presses of Down did nothing at all — the field
 *    is autofocused there, so the remote arrived on the screen already stuck.
 *  • A checkbox, a colour, a file button own NEITHER. Arrows do nothing on them
 *    natively, so there is nothing to protect and everything to gain.
 */
function typing(el: EventTarget | null, dir: Direction): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const across = dir === 'left' || dir === 'right';
  if (el.isContentEditable) return true;
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLSelectElement) return across;
  if (el.getAttribute('role') === 'slider') return across;
  if (el instanceof HTMLInputElement) {
    if (el.type === 'radio') return true;
    if (el.type === 'range' || TEXTUAL.has(el.type)) return across;
  }
  return false;
}

/**
 * TURN THE RING UP. Set once, from a real key event, and never unset — a viewer
 * who has picked up a remote has not put it down, and a flag that flickered off
 * on the next stray `pointermove` would take the focus ring with it in the
 * middle of using it.
 *
 * The attribute on <html> is what the stylesheet hangs the fatter ring off; the
 * boolean is what `focus.svelte.ts` reads to stand its ambient answer down.
 */
let remote = false;
function markRemote(): void {
  if (remote) return;
  remote = true;
  document.documentElement.dataset.dpad = 'on';
}

/**
 * TRUE ONCE A DIRECTIONAL KEY HAS BEEN SEEN — i.e. once there is a remote or a
 * keyboard driving this session, and never before.
 */
export function remoteDriving(): boolean {
  return remote;
}

/**
 * TRUE WHEN THIS SCREEN DECIDES WHAT AN ARROW MEANS WITH NOTHING FOCUSED.
 *
 * Exactly one screen does, and it is the player. Watching a film, the focus is
 * deliberately NOWHERE — a ring around a button would be drawn over the picture
 * for as long as the film ran — and in that state Left and Right mean ten
 * seconds, not "the nearest control to the right". No amount of geometry
 * produces a seek, so the player is asked instead.
 *
 * It is only the empty case. The moment something on the player IS focused —
 * the viewer has come into the transport bar — the arrows are spatial again and
 * this file handles them exactly as it handles a shelf.
 */
function ownsEmptyArrows(): boolean {
  return document.querySelector('[data-dpad-owner]') !== null;
}

/** Whether anything at all currently holds the focus. */
function occupied(): boolean {
  const el = document.activeElement;
  return el instanceof HTMLElement && el !== document.body;
}

function onKeydown(event: KeyboardEvent): void {
  // Somebody nearer the element has already spent this key: a menu's own
  // Up/Down, the scrubber's Left/Right, a sheet's Escape. Never take it twice.
  if (event.defaultPrevented) return;
  if (event.altKey || event.ctrlKey || event.metaKey) return;

  const dir = ARROWS[event.key];
  if (dir !== undefined) {
    if (typing(event.target, dir)) return;
    markRemote();
    if (!occupied() && ownsEmptyArrows()) return;
    // No candidate that way — hand the key back, so the page still scrolls past
    // a rail that has not loaded and a screen that is all prose.
    if (move(dir)) event.preventDefault();
    return;
  }

  /* Enter and Space are NOT a signal that a remote is driving, and it matters:
     a phone's on-screen keyboard sends Enter every time somebody searches, and
     `remoteDriving()` standing the ambient focus down on a phone would take the
     banner's trailer with it. They also need no handling — every candidate this
     file can reach is a link, a button or a field, all of which the browser
     already activates on Enter. */

  if (BACKS.has(event.key) || event.keyCode === 27) {
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;
    /* The player's Back closes a sheet, then steps out of the transport bar,
       then leaves the film — three levels this file cannot see. */
    if (ownsEmptyArrows()) return;
    /* A dialog answers its own Back — `<dialog>` fires `cancel`, and the sheets
       that are not dialogs listen for Escape themselves. Leaving the screen out
       from under an open sheet is the one thing Back must never do. */
    if (scope() !== document) return;
    event.preventDefault();
    back();
  }
}

let started = false;

/** Called once from `App.svelte`, in the same shape as `startRouter()`. */
export function startDpad(): () => void {
  if (started || typeof window === 'undefined') return () => {};
  started = true;
  window.addEventListener('keydown', onKeydown);
  // A dwell opens a card long after the press that focused it; the transition
  // finishing is the only event that says so. `settleSoon` stands down by
  // itself everywhere but a television with a remote in hand.
  window.addEventListener('transitionend', onTransitionEnd, { passive: true });
  return () => {
    window.removeEventListener('keydown', onKeydown);
    window.removeEventListener('transitionend', onTransitionEnd);
    started = false;
  };
}
